/**
 * The Business Unit Group create / edit dialog (f4.1, ownership added in f4.2).
 *
 * Built on the prototype's own shapes through f2.2's `Modal`:
 * `modal-scrim` / `modal` / `modal-head` / `modal-body` / `modal-foot` with
 * `m-sub`.
 *
 * Two things the dialog deliberately does NOT do:
 *
 * | Element | Why it is not here |
 * | --- | --- |
 * | A `Status` field | Status is not a property you type when you create a group: a new group is Active, and the only transition afterwards is deactivate or reactivate, which the table offers as a row action. A control that could set a group inactive at birth invites exactly one mistake and enables nothing |
 * | `required` on DEM and CEM | Ownership is an assignment, and a group is routinely recorded before anybody has decided who will run it, so neither owner can be demanded at create time |
 *
 * **Ownership is written as assignments, not as columns.** There is no
 * `dem_id` on the group: naming an owner is a `POST` to the assignments
 * endpoint, and replacing one is end-then-add, both audited and both
 * preserved. This dialog issues those requests after the group itself is
 * saved, so a refused assignment never loses the group.
 *
 * The deactivate confirmation lives in its own module: it owns a two-step
 * exchange with the server rather than a single submit.
 */

import { useMemo, useState } from 'react';
import { Check } from 'lucide-react';

import type { BusinessUnitGroup, LookupValue } from '@/api/client';
import {
  useAddBusinessUnitGroup,
  useAssignOwner,
  useAssignableUsers,
  useEndAssignment,
  useUpdateBusinessUnitGroup,
} from '@/api/queries';
import { DialogField, Modal } from '@/routes/reference-data/Modal';

import { generalMessage, problemFor } from './groupView';
import { GROUP_ROLE_CODES, ownerFor, type GroupRoleCode } from './ownershipView';

const NODE_TYPE = 'BUSINESS_UNIT_GROUP';

/** The account holding each role when the dialog opened, keyed by role code. */
type Ownership = Partial<Record<GroupRoleCode, string>>;

function currentOwnership(group: BusinessUnitGroup | null): Ownership {
  if (!group) return {};
  return Object.fromEntries(
    GROUP_ROLE_CODES.map((roleCode) => [roleCode, ownerFor(group, roleCode)?.user_id ?? '']),
  );
}

/**
 * Create and edit.
 *
 * On an edit only `name` is sent for the group itself, because `code` is
 * immutable: it is the business identifier the phase 8 import and other
 * systems match on, and the server does not accept it on a `PATCH`. The
 * control is rendered read-only rather than removed, so the value stays
 * visible where it was typed.
 */
export function GroupDialog({
  group,
  deliveryRoles,
  onClose,
}: {
  /** null for a create. */
  group: BusinessUnitGroup | null;
  /** The DELIVERY_ROLE values, straight from the lookup catalogue. */
  deliveryRoles: readonly LookupValue[];
  onClose: () => void;
}) {
  const editing = group !== null;
  const create = useAddBusinessUnitGroup();
  const update = useUpdateBusinessUnitGroup();
  const assign = useAssignOwner();
  const endAssignment = useEndAssignment();
  const candidates = useAssignableUsers();

  const [code, setCode] = useState(group?.code ?? '');
  const [name, setName] = useState(group?.name ?? '');
  const [owners, setOwners] = useState<Ownership>(() => currentOwnership(group));

  const users = candidates.data?.users ?? [];
  const before = useMemo(() => currentOwnership(group), [group]);

  /**
   * The role id for a role code, from the catalogue.
   *
   * The two codes are the only thing this screen names; every id and every
   * label is the server's, so a role renamed in the lookup renames itself
   * here with no change.
   */
  const roleIdFor = (roleCode: GroupRoleCode): string | undefined =>
    deliveryRoles.find((role) => role.code === roleCode && role.is_active)?.id;

  const failure = create.error ?? update.error ?? assign.error ?? endAssignment.error;
  const busy =
    create.isPending || update.isPending || assign.isPending || endAssignment.isPending;

  /**
   * Write the ownership changes, one role at a time, then close.
   *
   * Only roles whose chosen account DIFFERS from the one already recorded are
   * touched: re-saving a dialog nobody edited must not append an assignment
   * that says the same thing, because every write is audited and a log full
   * of no-op assignments is a log nobody reads.
   */
  async function applyOwnership(groupId: string) {
    for (const roleCode of GROUP_ROLE_CODES) {
      const chosen = owners[roleCode] ?? '';
      if (chosen === (before[roleCode] ?? '')) continue;

      const held = group ? ownerFor(group, roleCode) : undefined;
      if (held) {
        // End then add: nothing is deleted, and who held the role before
        // stays on the record and in the audit log.
        await endAssignment.mutateAsync({
          assignmentId: held.assignment_id,
          nodeType: NODE_TYPE,
          nodeId: groupId,
        });
      }

      const deliveryRoleId = chosen ? roleIdFor(roleCode) : undefined;
      if (!chosen || !deliveryRoleId) continue;

      await assign.mutateAsync({
        user_id: chosen,
        delivery_role_id: deliveryRoleId,
        node_type: NODE_TYPE,
        node_id: groupId,
      });
    }
  }

  async function submit() {
    try {
      // The group is saved first. If an assignment is then refused the dialog
      // stays open with the refusal on screen, and the group is not lost.
      const saved = editing
        ? await update.mutateAsync({ groupId: group.id, change: { name } })
        : await create.mutateAsync({ code, name });

      await applyOwnership(saved.id);
      onClose();
    } catch {
      // The mutation that failed is holding the refusal, and `failure` above
      // renders it. Swallowing the rejection here only stops it becoming an
      // unhandled one; nothing is hidden from the reader.
    }
  }

  return (
    <Modal
      title={editing ? 'Edit Business Unit Group' : 'New Business Unit Group'}
      subtitle="Top level of the hierarchy. Business Units hang off it"
      onClose={onClose}
      onSubmit={() => {
        void submit();
      }}
      busy={busy}
      submitLabel="Save group"
      submitIcon={<Check aria-hidden="true" />}
      layout="plain"
      // Shown only when the server refused without naming a field, so a
      // field-level problem is never duplicated at the top of the dialog.
      message={generalMessage(failure, 'code', 'name', 'user_id', 'delivery_role_id')}
    >
      <fieldset className="mb-4">
        <div className="fieldset-title">Identity</div>
        <div className="grid grid-3">
          <DialogField
            label="Code"
            htmlFor="bug-code"
            problem={problemFor(failure, 'code')}
            hint={
              editing
                ? 'Immutable. Other systems match on it'
                : 'Unique business identifier'
            }
          >
            <input
              id="bug-code"
              type="text"
              value={code}
              readOnly={editing}
              required
              placeholder="AM"
              onChange={(event) => setCode(event.target.value)}
            />
          </DialogField>
          <DialogField
            label="Name"
            htmlFor="bug-name"
            span
            problem={problemFor(failure, 'name')}
          >
            <input
              id="bug-name"
              type="text"
              value={name}
              required
              placeholder="CU Asset Management"
              onChange={(event) => setName(event.target.value)}
            />
          </DialogField>
        </div>
      </fieldset>

      <fieldset>
        <div className="fieldset-title">Ownership</div>
        <div className="grid grid-2">
          {GROUP_ROLE_CODES.map((roleCode) => (
            <DialogField
              key={roleCode}
              label={deliveryRoles.find((role) => role.code === roleCode)?.name ?? roleCode}
              htmlFor={`bug-owner-${roleCode}`}
              problem={problemFor(failure, 'user_id')}
              hint={
                candidates.isPending
                  ? 'Loading the accounts'
                  : users.length === 0
                    ? 'No account can be named yet. Add one on the Users screen first'
                    : 'Optional. Ownership decides access'
              }
            >
              <select
                id={`bug-owner-${roleCode}`}
                value={owners[roleCode] ?? ''}
                disabled={candidates.isPending || users.length === 0}
                onChange={(event) =>
                  setOwners((current) => ({ ...current, [roleCode]: event.target.value }))
                }
              >
                <option value="">No {roleCode} yet</option>
                {/* The address disambiguates two accounts sharing a display
                    name, exactly as the assign dialog's picker does. */}
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.display_name} ({user.email})
                  </option>
                ))}
              </select>
            </DialogField>
          ))}
        </div>
      </fieldset>
    </Modal>
  );
}
